import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import Ticker from "../components/Ticker";
import Footer from "../components/Footer";
import { Check, Loader2, Gift, Lock, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const BENEFITS = [
  "Unlimited access to every audio narration — studio-grade broadcaster voices",
  "Full Premium research library and Desk notes",
  "Weekly Editor's Ledger delivered Sunday morning",
  "The Wire — filter live feeds by source, keyword, sector",
  "Complete Archive access with search and download",
  "Priority access to new coverage sections",
];

const FREE_TIER_BENEFITS = [
  "Selected articles",
  "Market commentary",
  "Weekly newsletter",
  "Limited access to premium articles each month",
  "Community access",
];

const PREMIUM_TIER_BENEFITS = [
  "Full access to daily articles",
  "Complete publication archive",
  "Weekly market and geopolitical reports",
  "Downloadable PDF reports",
  "Email alerts and notifications",
  "Ad-free experience",
];

const PRO_TIER_BENEFITS = [
  "Everything in Premium, plus:",
  "Institutional-quality research reports",
  "Strategic intelligence publications",
  "Long-term macro & geopolitical forecasts",
  "Exclusive white papers & special reports",
  "Early access to major publications",
  "Quarterly outlook reports",
  "Premium research archive",
];

const TIER_BENEFITS = {
  premium: PREMIUM_TIER_BENEFITS,
  professional: PRO_TIER_BENEFITS,
};

const TIER_TAGLINE = {
  premium:
    "For individual investors, professionals, advisors, and readers who want in-depth analysis.",
  professional:
    "For portfolio managers, economists, and institutions needing strategic research.",
};

const TIER_LABEL = {
  premium: "Premium",
  professional: "Professional",
};

const FreeTierCard = ({ isAuthed, onSignup }) => (
  <div
    data-testid="package-card-free"
    className="relative bg-white border-2 border-neutral-300 p-6 md:p-7 flex flex-col"
  >
    <div className="text-[10px] tracking-[0.35em] uppercase text-neutral-500 font-condensed font-bold">
      Free
    </div>
    <div className="mt-3 flex items-baseline gap-1">
      <span className="font-serif-display text-[42px] font-bold text-[#0B1F33]">$0</span>
      <span className="text-[13px] text-neutral-600">/ forever</span>
    </div>
    <p className="mt-2 text-[13px] text-neutral-700">
      Read the daily feed and market wire. No card, no commitment.
    </p>
    <ul className="mt-4 space-y-2 text-[12.5px] text-neutral-700 flex-1">
      {FREE_TIER_BENEFITS.map((b) => (
        <li key={b} className="flex items-start gap-2">
          <Check className="w-3.5 h-3.5 text-[#0891B2] mt-1 shrink-0" />
          <span>{b}</span>
        </li>
      ))}
    </ul>
    <button
      type="button"
      onClick={onSignup}
      data-testid="free-tier-cta"
      className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 text-[12px] tracking-[0.28em] uppercase font-condensed font-bold border-2 border-[#0B1F33] text-[#0B1F33] hover:bg-[#0B1F33] hover:text-[#FFFFFF] transition-colors"
    >
      {isAuthed ? "You&apos;re on the free tier" : "Continue with free"}
    </button>
  </div>
);

const PackageCard = ({ pkg, popular, onSelect, loading, monthlyEquivalent }) => (
  <div
    data-testid={`package-card-${pkg.lookup_key}`}
    className={`relative bg-white border-2 p-6 md:p-7 flex flex-col ${
      popular ? "border-[#B8E9F1]" : "border-neutral-300"
    }`}
  >
    {popular && (
      <div className="absolute -top-3 left-6 bg-[#B8E9F1] text-[#0B1F33] px-3 py-1 font-condensed uppercase tracking-[0.22em] text-[10px] font-bold">
        Most Popular
      </div>
    )}
    <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-condensed font-bold">
      {TIER_LABEL[pkg.tier] || pkg.tier}
    </div>
    <div className="mt-3 flex items-baseline gap-1">
      <span className="font-serif-display text-[42px] font-bold text-[#0B1F33]">
        ${Math.floor(pkg.amount)}
      </span>
      <span className="text-[13px] text-neutral-600">
        / {pkg.interval === "year" ? "year" : "month"}
      </span>
    </div>
    {pkg.interval === "year" && monthlyEquivalent && (
      <div className="mt-1 text-[11px] tracking-[0.14em] uppercase text-[#0b6b3a] font-semibold">
        ≈ ${monthlyEquivalent}/mo · save {pkg.savingsPct}%
      </div>
    )}
    {TIER_TAGLINE[pkg.tier] && (
      <p className="mt-2 text-[13px] text-neutral-700 leading-snug">
        {TIER_TAGLINE[pkg.tier]}
      </p>
    )}
    {pkg.trial_days > 0 && (
      <div className="mt-3 inline-flex self-start items-center gap-1.5 bg-[#0B1F33] text-[#FFFFFF] px-2 py-1 font-condensed uppercase tracking-[0.2em] text-[10px] font-bold">
        <Gift className="w-3 h-3 text-[#0891B2]" /> {pkg.trial_days}-day free trial
      </div>
    )}
    {TIER_BENEFITS[pkg.tier] && (
      <ul className="mt-4 space-y-2 text-[12.5px] text-neutral-700 flex-1">
        {TIER_BENEFITS[pkg.tier].map((b, i) => (
          <li key={b} className="flex items-start gap-2">
            {i === 0 && pkg.tier === "professional" ? (
              <span className="w-3.5 h-3.5 mt-1 shrink-0" />
            ) : (
              <Check className="w-3.5 h-3.5 text-[#0891B2] mt-1 shrink-0" />
            )}
            <span className={i === 0 && pkg.tier === "professional" ? "font-semibold text-[#0B1F33]" : ""}>
              {b}
            </span>
          </li>
        ))}
      </ul>
    )}
    <button
      onClick={() => onSelect(pkg)}
      disabled={loading}
      data-testid={`checkout-btn-${pkg.lookup_key}`}
      className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 text-[12px] tracking-[0.28em] uppercase font-condensed font-bold transition-colors disabled:opacity-60 ${
        popular
          ? "bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33]"
          : "bg-[#0B1F33] hover:bg-[#132f57] text-[#FFFFFF]"
      }`}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
      Start Free Trial
    </button>
  </div>
);

export const SubscribePage = () => {
  const [packages, setPackages] = useState([]);
  const [interval, setInterval] = useState("month"); // "month" | "year"
  const [loading, setLoading] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const { user, signIn, refresh, requireAuth } = useAuth();

  useEffect(() => {
    api.get("/subscribe/packages").then((r) => setPackages(r.data.packages || []));
    // If returning from Stripe, poll for status
    const sid = new URLSearchParams(window.location.search).get("session_id");
    const cancelled = new URLSearchParams(window.location.search).get("cancelled");
    if (cancelled) {
      toast("Checkout cancelled — you can try again anytime.");
    }
    if (sid) {
      setStatusLoading(true);
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await api.get(`/subscribe/status/${sid}`);
          if (r.data.granted) {
            toast.success("You're in — welcome to Vexent Wire.");
            setStatusLoading(false);
            refresh();
            window.history.replaceState({}, "", "/subscribe");
            return;
          }
          if (r.data.status === "expired") {
            toast.error("Checkout session expired. Please try again.");
            setStatusLoading(false);
            return;
          }
        } catch {
          /* keep polling */
        }
        if (attempts < 6) {
          setTimeout(poll, 2000);
        } else {
          setStatusLoading(false);
        }
      };
      poll();
    }
  }, [refresh]);

  const startCheckout = async (pkg) => {
    if (!user) {
      signIn();
      return;
    }
    setLoading(pkg.lookup_key);
    try {
      const r = await api.post("/subscribe/checkout", {
        lookup_key: pkg.lookup_key,
        origin_url: window.location.origin,
      });
      if (r.data.url) {
        window.location.href = r.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      toast.error("Checkout failed", {
        description: err?.response?.data?.detail || err.message,
      });
      setLoading(null);
    }
  };

  // Filter to the selected interval and pair annual with monthly-equivalent copy
  const monthlyByTier = Object.fromEntries(
    packages.filter((p) => p.interval === "month").map((p) => [p.tier, p.amount])
  );
  const shownPackages = packages
    .filter((p) => p.interval === interval)
    .map((p) => {
      if (p.interval === "year" && monthlyByTier[p.tier]) {
        const monthlyEquivalent = (p.amount / 12).toFixed(2).replace(/\.00$/, "");
        const monthlyTotal = monthlyByTier[p.tier] * 12;
        const savingsPct = Math.round(((monthlyTotal - p.amount) / monthlyTotal) * 100);
        return { ...p, monthlyEquivalent, savingsPct };
      }
      return p;
    });

  return (
    <div>
      <Ticker />
      <Header />
      <div className="max-w-[1200px] mx-auto px-6 py-12 md:py-16">
        {statusLoading && (
          <div className="mb-8 border border-[#B8E9F1] bg-[#fdf7ec] p-4 flex items-center gap-3">
            <Loader2 className="w-4 h-4 animate-spin text-[#0891B2]" />
            <span className="text-[14px] text-[#0B1F33]">
              Confirming your payment…
            </span>
          </div>
        )}
        <div className="text-center max-w-[720px] mx-auto">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-condensed font-bold">
            <Sparkles className="w-3.5 h-3.5" /> Vexent Wire Premium
          </div>
          <h1 className="mt-4 font-serif-display text-[38px] md:text-[52px] font-bold text-[#0B1F33] leading-[1.1]">
            Independent research. Unfiltered analysis.
          </h1>
          <p className="mt-4 text-[17px] text-neutral-700 leading-relaxed">
            Read the daily feed for free — no signup, no card. Ready for the
            full research library and audio archive? Start a 30-day free
            trial on Premium or Professional. Card required; cancel anytime
            before the trial ends and you won&apos;t be charged.
          </p>
        </div>

        <div className="mt-10 flex justify-center">
          <div
            role="tablist"
            aria-label="Billing interval"
            className="inline-flex border-2 border-[#0B1F33] bg-white"
            data-testid="interval-toggle"
          >
            <button
              role="tab"
              aria-selected={interval === "month"}
              onClick={() => setInterval("month")}
              data-testid="interval-monthly-btn"
              className={`px-5 py-2 text-[11px] tracking-[0.24em] uppercase font-condensed font-bold transition-colors ${
                interval === "month"
                  ? "bg-[#0B1F33] text-[#FFFFFF]"
                  : "text-[#0B1F33] hover:bg-neutral-100"
              }`}
            >
              Monthly
            </button>
            <button
              role="tab"
              aria-selected={interval === "year"}
              onClick={() => setInterval("year")}
              data-testid="interval-annual-btn"
              className={`px-5 py-2 text-[11px] tracking-[0.24em] uppercase font-condensed font-bold transition-colors inline-flex items-center gap-2 ${
                interval === "year"
                  ? "bg-[#0B1F33] text-[#FFFFFF]"
                  : "text-[#0B1F33] hover:bg-neutral-100"
              }`}
            >
              Annual
              <span
                className={`px-1.5 py-0.5 text-[9px] tracking-normal font-bold ${
                  interval === "year"
                    ? "bg-[#B8E9F1] text-[#0B1F33]"
                    : "bg-[#0B1F33] text-[#FFFFFF]"
                }`}
              >
                SAVE ~17%
              </span>
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <FreeTierCard
            isAuthed={Boolean(user)}
            onSignup={() => {
              if (user) {
                toast.success("You're already on the free tier.", {
                  description: "Upgrade to Premium anytime for the full library.",
                });
              } else {
                requireAuth("free");
              }
            }}
          />
          {shownPackages.map((p) => (
            <PackageCard
              key={p.lookup_key}
              pkg={p}
              popular={p.tier === "premium"}
              onSelect={startCheckout}
              loading={loading === p.lookup_key}
              monthlyEquivalent={p.monthlyEquivalent}
            />
          ))}
        </div>

        {/* Benefits detail */}
        <div className="mt-16 max-w-[820px] mx-auto">
          <div className="inline-block relative pb-2 border-b-[3px] border-[#B8E9F1]">
            <h2 className="font-condensed uppercase text-[22px] font-bold text-[#0B1F33] tracking-tight">
              Everything a subscriber gets
            </h2>
          </div>
          <ul className="mt-6 grid md:grid-cols-2 gap-3">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-3 text-[15px] text-[#0B1F33]">
                <Check className="w-4 h-4 text-[#0891B2] mt-1 shrink-0" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-14 max-w-[820px] mx-auto border-t border-neutral-300 pt-8 flex items-start gap-3 text-[13px] text-neutral-600">
          <Lock className="w-4 h-4 text-[#0891B2] mt-0.5 shrink-0" />
          <div>
            Payments processed securely through Stripe. Premium and
            Professional plans include a 30-day free trial; your card is
            required at signup and will be charged automatically at the
            selected rate when the trial ends. Cancel anytime before the
            trial ends to avoid being charged, or cancel later from your
            account settings to stop future renewals — access continues
            through the end of your current billing period. See our{" "}
            <a href="/legal/billing" className="underline hover:text-[#0891B2]">Billing &amp; Refunds</a>
            {" "}policy for details.
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SubscribePage;
